import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { QueueConfiguration, SubjectImagePreferences, Case, Opinion } from 'services';
import { ReviewContext } from './ReviewContextProvider';
import { CaseCard } from 'components/shared';
import { getFeedbackTraitCount } from '../util/OpinionHelpers';
import { AdminContext, AdminContextProps } from 'Store/Admin';
import { DEFAULT_MIN_ANIMATION_LOOP_SPEED } from 'components/admin/AnimationLoopSpeedField';

export interface ReviewCaseCardGridProps {
  config: QueueConfiguration;
  settings: SubjectImagePreferences;
  className?: string;
  setZoomInIsChecked: (boolean) => void;
  onZoomClick: (item: Case, num?: number) => void;
}

export const ReviewCaseCardGrid: React.FunctionComponent<ReviewCaseCardGridProps> = props => {
  const { state, dispatch } = useContext(ReviewContext);
  const adminContext: AdminContextProps = useContext(AdminContext);
  const divRef = useRef<HTMLDivElement>(null);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.altKey === true && (event.key === 'n' || event.code === 'KeyN')) {
        // Alt + n: Apply noplicy violation
        const opinion = new Opinion();
        opinion.comment = 'No Policy Violation applied with Hotkey';
        opinion.opinionCategoryLabels = { None: ['No Policy Violation'] };
        dispatch({ type: 'PUT_OPINION', payload: opinion });
        dispatch({ type: 'UNSELECT_ALL' });

        // Bit of a hack here:
        // We need this focus line to close the trait selection dropdown. Once the opinion has been put the
        // dropdown will be deactivated, but unfortunately this doesn't close an open dropdown, just prevents
        // opening a new one. Grabbing focus is the easiest way to fix this, and is acceptable given the use case
        // here is a moderator operating through keyboard hot keys.
        if (divRef.current) {
          divRef.current?.click();
        }
      } else if (event.altKey === true && (event.key === 's' || event.code === 'KeyS')) {
        // Alt + s: select next case
        for (let innerIndex = 0; innerIndex < state.cases.length; innerIndex++) {
          const item = state.cases[innerIndex];
          if (!item.opinion && !item.isSelected) {
            dispatch({ type: 'SELECT_CASE', payload: [item.case] });
            break;
          }
        }
      } else if (event.altKey === true && (event.key === '.' || event.code === 'Period')) {
        if (state.cases.some(reviewCase => reviewCase.isSelected)) {
          dispatch({ type: 'DISABLE_CAROUSEL_ANIMATION' });
          dispatch({ type: 'UPDATE_NEXT_IMAGE_COUNT', payload: state.nextImgCount + 1 });
        }
      } else if (event.altKey === true && (event.key === ',' || event.code === 'Comma')) {
        if (state.cases.some(reviewCase => reviewCase.isSelected)) {
          dispatch({ type: 'DISABLE_CAROUSEL_ANIMATION' });
          dispatch({ type: 'UPDATE_PREVIOUS_IMAGE_COUNT', payload: state.previousImgCount + 1 });
        }
      }
    },
    [state.cases, state.nextImgCount, state.previousImgCount]
  );

  useEffect(() => {
    // if the cases change we'll want a new event listener tied to the new cases, so scrap the old one if it exists
    document.removeEventListener('keydown', handleKeyPress);
    // attach the event listener
    document.addEventListener('keydown', handleKeyPress);

    // remove the event listener on component breakdown
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress, state.cases]);

  useEffect(() => {
    // synchronize the carousel animation setting from admin settings to the reviewContext carouselAnimationEnabled variable
    if (adminContext.state.queueDataState?.uxPreferences.subjectJson.animationLoopIsActive) {
      dispatch({ type: 'ENABLE_CAROUSEL_ANIMATION' });
      const animationLoopSpeed = adminContext.state.queueDataState?.uxPreferences.subjectJson.animationLoopSpeed;
      if (animationLoopSpeed !== undefined) {
        dispatch({ type: 'SET_ANIMATION_LOOP_SPEED', payload: animationLoopSpeed });
      } else {
        // if animation loop is enabled and there is no defined animation loop speed, set default speed
        dispatch({
          type: 'SET_ANIMATION_LOOP_SPEED',
          payload: DEFAULT_MIN_ANIMATION_LOOP_SPEED
        });
      }
    }
  }, []);

  return (
    <div className={`ms-Grid pl-5 m-0 '${props.className || ''}`} dir="ltr" ref={divRef}>
      {state.cases.map((item, index) => {
        const feedbackOrdered = item.feedback.sort(
          (a, b) => new Date(b.feedbackDateTime).getTime() - new Date(a.feedbackDateTime).getTime()
        );
        const tags = getFeedbackTraitCount(feedbackOrdered);
        let reportedTrait = Object.entries(tags)
          .sort((a, b) => a[1] - b[1])
          .pop();

        const isEqual =
          Object.entries(tags)[0] && Object.entries(tags).every(val => val[1] === Object.entries(tags)[0][1]);

        if (isEqual) {
          reportedTrait = Object.entries(tags)[Object.entries(tags).length - 1];
        }

        return (
          <div key={index} className="ms-Grid-col ms-md12 ms-lg6 ms-xxl4 ms-xxxl3 p-0 pr-3 mb-3 pl-2">
            <CaseCard
              index={index + 1}
              checked={item.isSelected}
              case={item.case}
              config={props.config}
              opinion={item.opinion}
              reportedTrait={reportedTrait && reportedTrait[0]}
              traitCount={Object.keys(tags).length - 1}
              settings={props.settings}
              onZoomClick={props.onZoomClick}
              onUndoClick={(): void => {
                dispatch({ type: 'RESET_OPINION', payload: [item.case] });
              }}
              onChange={(checked): void => {
                const actionType = checked ? 'SELECT_CASE' : 'UNSELECT_CASE';
                dispatch({ type: actionType, payload: [item.case] });
              }}
              setZoomInIsChecked={props.setZoomInIsChecked}
            />
          </div>
        );
      })}
    </div>
  );
};
